import { ApolloError, useApolloClient, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  SERVICE_CATEGORIES_QUERY,
  SERVICE_WITH_CONFIGURATION_QUERY,
  UPSERT_SERVICE_WITH_MATRIX_PRICING_MUTATION,
} from "@/graphql/services";
import { useGraphQLFormattedErrorHandler } from "@/lib/graphql/error-handler";
import { useSalonStore } from "@/lib/store/salon";
import type { ServiceOptionGroupFormData } from "../types";
import { type ServiceEditFormData, serviceEditSchema } from "../validation/service-edit-schema";

interface UseServiceEditProps {
  serviceId: string;
}

export function useServiceEdit({ serviceId }: UseServiceEditProps) {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;
  const { processApolloError } = useGraphQLFormattedErrorHandler();
  const apolloClient = useApolloClient();

  // Local state for mutations
  const [mutationError, setMutationError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [service, setService] = useState<any>(null);
  const [error, setError] = useState<Error | null>(null);

  // Single atomic upsert mutation (senior-level UpsertService approach)
  const [upsertServiceWithMatrixPricing, { error: upsertError }] = useMutation(
    UPSERT_SERVICE_WITH_MATRIX_PRICING_MUTATION,
    {
      errorPolicy: "all", // This ensures errors are returned and not thrown automatically
    }
  );

  // Async function to fetch and transform data for form initialization
  const fetchDefaultValues = useCallback(async (): Promise<ServiceEditFormData> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch service and categories in parallel
      const [serviceResult, categoriesResult] = await Promise.all([
        apolloClient.query({
          query: SERVICE_WITH_CONFIGURATION_QUERY,
          variables: { salonId: salonId || "", serviceId: serviceId || "" },
          fetchPolicy: "network-only",
        }),
        apolloClient.query({
          query: SERVICE_CATEGORIES_QUERY,
          variables: { salonId: salonId || "" },
          fetchPolicy: "cache-first",
        }),
      ]);

      const fetchedService = serviceResult.data?.serviceWithConfiguration;
      const fetchedCategories = categoriesResult.data?.serviceCategories || [];

      // Store in state for later use
      setService(fetchedService);
      setCategories(fetchedCategories);

      if (!fetchedService) {
        throw new Error("Service not found");
      }

      // Transform service option groups to form data
      const optionGroups: ServiceOptionGroupFormData[] = (
        fetchedService.serviceOptionGroups || []
      ).map((group) => ({
        id: group.id,
        name: group.name,
        selectionType: group.selectionType,
        minSelections: group.minSelections,
        maxSelections: group.maxSelections || undefined,
        sortOrder: group.sortOrder,
        options: (group.options || []).map((option) => ({
          id: option.id,
          optionName: option.optionName,
          priceModifier: option.priceModifier,
          durationModifier: option.durationModifier,
          isDefault: option.isDefault,
          sortOrder: option.sortOrder,
        })),
      }));

      return {
        templateId: fetchedService.templateId ?? null,
        basicInfo: {
          name: fetchedService.name || "",
          description: fetchedService.description || "",
          categoryId: fetchedService.categoryId || "",
          serviceCode: fetchedService.serviceCode || "",
          duration: fetchedService.duration ?? 60,
          isActive: fetchedService.isActive ?? true,
          allowOnlineBooking: fetchedService.allowOnlineBooking ?? true,
          storefrontDisplayMode: fetchedService.storefrontDisplayMode ?? null,
          isPopular: fetchedService.isPopular ?? false,
        },
        pricing: {
          pricingMode: fetchedService.pricingMode,
          basePrice: fetchedService.basePrice || 0,
        },
        optionGroups,
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to load service");
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apolloClient, salonId, serviceId]);

  // Create unified form with async defaultValues
  const form = useForm<ServiceEditFormData>({
    resolver: zodResolver(serviceEditSchema),
    defaultValues: fetchDefaultValues,
    mode: "onChange",
  });

  // Transform complete service data for UpsertService backend (senior-level approach)
  const transformCompleteServiceInput = useCallback((formData: ServiceEditFormData) => {
    return {
      // Template reference (null = custom config)
      templateId: formData.templateId !== undefined ? formData.templateId : null,
      // Basic service information
      basicInfo: {
        name: formData.basicInfo.name,
        description: formData.basicInfo.description,
        categoryId: formData.basicInfo.categoryId || undefined,
        serviceCode: formData.basicInfo.serviceCode,
        duration: formData.basicInfo.duration,
        isActive: formData.basicInfo.isActive,
        allowOnlineBooking: formData.basicInfo.allowOnlineBooking,
        storefrontDisplayMode: formData.basicInfo.storefrontDisplayMode ?? null,
        isPopular: formData.basicInfo.isPopular,
      },

      // Pricing configuration
      pricing: {
        pricingMode: formData.pricing.pricingMode,
        basePrice: formData.pricing.basePrice, // Already in cents from CurrencyInput
      },

      // Complete option groups with nested options (UpsertService will handle CRUD)
      optionGroups: formData.optionGroups.map((group) => ({
        id: group.id || undefined, // Backend uses this for create/update detection
        name: group.name,
        selectionType: group.selectionType,
        minSelections: group.minSelections,
        maxSelections: group.maxSelections || undefined,
        sortOrder: group.sortOrder,
        options: group.options.map((option) => ({
          id: option.id || undefined, // Backend uses this for create/update detection
          optionName: option.optionName,
          priceModifier: option.priceModifier, // Already in cents from CurrencyInput
          durationModifier: option.durationModifier,
          isDefault: option.isDefault,
          sortOrder: option.sortOrder,
        })),
      })),
    };
  }, []);

  // Senior-level atomic save function using single UpsertService endpoint
  const save = useCallback(async () => {
    if (!form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }

    if (!salonId || !serviceId) {
      toast.error("Missing salon or service ID");
      return;
    }

    // Validate form before proceeding
    const isValid = await form.trigger();
    if (!isValid) {
      const errors = form.formState.errors;

      // Build a helpful error message
      const errorMessages: string[] = [];
      if (errors.basicInfo) {
        Object.entries(errors.basicInfo).forEach(([key, error]) => {
          if (
            error &&
            typeof error === "object" &&
            "message" in error &&
            typeof error.message === "string"
          ) {
            errorMessages.push(`${key}: ${error.message}`);
          }
        });
      }
      if (errors.pricing) {
        Object.entries(errors.pricing).forEach(([key, error]) => {
          if (
            error &&
            typeof error === "object" &&
            "message" in error &&
            typeof error.message === "string"
          ) {
            errorMessages.push(`${key}: ${error.message}`);
          }
        });
      }
      if (errors.optionGroups) {
        errorMessages.push("Option groups have validation errors - check console for details");
      }

      const errorText =
        errorMessages.length > 0
          ? `Validation errors: ${errorMessages.join(", ")}`
          : "Please fix the validation errors before saving";

      toast.error(errorText);
      return;
    }

    setSaving(true);
    setMutationError(null);

    try {
      const currentFormData = form.getValues();

      // Transform complete service state for atomic upsert
      const input = transformCompleteServiceInput(currentFormData);

      // Single atomic mutation - UpsertService pattern
      const result = await upsertServiceWithMatrixPricing({
        variables: {
          salonId,
          serviceId,
          input,
        },
        refetchQueries: [
          {
            query: SERVICE_WITH_CONFIGURATION_QUERY,
            variables: { salonId, serviceId },
          },
        ],
      });

      // Check for GraphQL errors first (since errorPolicy is 'all', errors don't throw automatically)
      if (result.errors && result.errors.length > 0) {
        // Create an ApolloError from the GraphQL errors
        throw new ApolloError({
          graphQLErrors: result.errors,
          networkError: null,
          errorMessage: result.errors[0]?.message || "GraphQL error occurred",
        });
      }

      // Only check for missing data if there were no GraphQL errors
      if (!result.data?.upsertServiceWithMatrixPricing) {
        throw new Error("No data returned from upsert operation");
      }

      // Reset form to clean state with updated data
      form.reset(form.getValues());

      toast.success("Service updated successfully");
    } catch (error) {
      // Handle ApolloError specifically
      if (error instanceof ApolloError) {
        const processedErrors = processApolloError(error);

        // Check if it's a service code already exists error by checking the actual error code
        const hasServiceCodeError = error.graphQLErrors?.some(
          (gqlError) => gqlError.extensions?.code === "serviceCodeAlreadyExists"
        );

        if (hasServiceCodeError) {
          // Set the error on the specific field
          form.setError("basicInfo.serviceCode", {
            type: "server",
            message:
              processedErrors[0]?.message ||
              "This service code is already in use. Please choose a different service code.",
          });
          // Don't show toast error, let the field validation handle it
          return; // Early return to prevent generic error handling
        }
        // Handle other GraphQL errors
        const errorMessage = processedErrors[0]?.message || "Save failed";
        toast.error(`Save failed: ${errorMessage}`);
        setMutationError(new Error(errorMessage));
      } else {
        // Handle non-Apollo errors
        const mutationErr = error instanceof Error ? error : new Error("Save failed");
        setMutationError(mutationErr);
        const errorMessage = mutationErr.message || "Unknown error occurred";
        toast.error(`Save failed: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  }, [
    form,
    salonId,
    serviceId,
    transformCompleteServiceInput,
    upsertServiceWithMatrixPricing,
    processApolloError,
  ]);

  // Discard changes - reset to original form state
  const discard = useCallback(async () => {
    setMutationError(null);
    // Re-fetch and reset form to original values
    const freshValues = await fetchDefaultValues();
    form.reset(freshValues);
    toast.info("Changes discarded");
  }, [form, fetchDefaultValues]);

  // Refetch service data
  const refetchService = useCallback(async () => {
    const freshValues = await fetchDefaultValues();
    form.reset(freshValues);
  }, [fetchDefaultValues, form]);

  // Combine all errors
  const combinedError = error || mutationError || upsertError;

  return {
    // Data
    service,
    categories,
    form,

    // State
    loading,
    saving,
    error: combinedError,

    // Actions
    save,
    discard,
    refetchService,
  };
}
